import { compact, isEmpty, sortBy } from 'lodash/fp';

import {
  ConsiderationContent,
  Offer,
  Placeholder,
  TableOfContentsItem,
} from '../../../../types/__codegen__/sanity/graphql';
import { ConsiderationPageSlug } from '../../types';
import getCustomSections from './get-custom-sections';
import getFormattedSections from './get-formatted-sections';
import mapContactContent from './map-contact-content';
import mapHeaderContent from './map-header-content';
import mapOfferContent from './map-offer-content';
import mapSellingPointsContent from './map-selling-points-content';
import mapTermsAndConditionsContent from '../../../../utils/map-terms-and-conditions-content';

type GetContentToRenderProps = {
  cmsData: {
    contact?: {
      items: TableOfContentsItem[];
      name: string;
    };
    considerationContent: ConsiderationContent;
  };
  offerContentToRender: {
    overrideOffers?: Offer[];
    placeholders: Placeholder[];
    targetSegment: string;
  } | null;
  slug: ConsiderationPageSlug;
};

const getContentToRender = ({ cmsData, offerContentToRender, slug }: GetContentToRenderProps) => {
  const considerationContentData = cmsData?.considerationContent;
  const contactData = cmsData?.contact;

  const sectionsArray = [];

  if (!isEmpty(offerContentToRender)) {
    const offerSection = mapOfferContent(offerContentToRender);
    sectionsArray.push(offerSection);
  }

  const headerSection = mapHeaderContent({
    cmsData: considerationContentData,
    removePaddingTop: !isEmpty(offerContentToRender),
    slug,
  });
  sectionsArray.push(headerSection);

  sectionsArray.push(...getCustomSections(slug));

  const contentSections = considerationContentData?.refersToContent;
  const sectionsWithIndex = (contentSections ?? []).map((section, index) => ({
    ...section,
    orderIndex: index + 1,
  }));

  const sellingPointsSection = mapSellingPointsContent({ refersToContent: sectionsWithIndex });
  sectionsArray.push(sellingPointsSection);

  const formattedSections = getFormattedSections(sectionsWithIndex);

  const contactSection = mapContactContent({ cmsData: contactData });
  sectionsArray.push(contactSection);

  if (!isEmpty(considerationContentData?.termsAndConditions)) {
    const mappedTermsAndConditions = mapTermsAndConditionsContent({
      cmsSectionContent: [considerationContentData.termsAndConditions],
    });
    sectionsArray.push({ ...mappedTermsAndConditions, orderIndex: 999 }); // Terms and Conditions section to be shown last on the page, after Contact section
  }

  const orderedSections = sortBy('orderIndex', compact([...sectionsArray, ...formattedSections]));

  return {
    ...cmsData,
    sections: orderedSections,
  };
};

export default getContentToRender;
